import React, { useState, useEffect, useRef } from 'react';
import {
  Landmark, Gem, Wallet, Car, TrendingUp, TrendingDown, Briefcase, AlertCircle, Info, Pencil,
  CheckCircle2, Wrench
} from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';
import { cn } from '../../lib/utils';

/**
 * Patron'un 5 saniyede tüm şirket durumunu okuduğu 6 ana kart.
 *
 * Muhasebe prensipleri:
 * - Araç alımı GİDER DEĞİLDİR (varlık dönüşümü)
 * - Kasadan çıkan para stoka bağlandı → toplam varlık değişmez
 * - Öz Sermaye = Başlangıç + Net Kâr − İşletme Giderleri
 */
const Card = ({ title, value, subtitle, icon: Icon, accent = 'default', testId, badge, warning, onEdit }) => {
  const accentMap = {
    default: 'border-border bg-card',
    gold: 'border-ti-gold/40 bg-gradient-to-br from-ti-gold/10 to-card shadow-[inset_0_0_0_1px_rgba(197,162,103,0.15)]',
    cash: 'border-border bg-card',
    profit: 'border-emerald-500/30 bg-card',
    loss: 'border-rose-500/40 bg-card',
    warning: 'border-amber-500/40 bg-card',
  };
  const iconColorMap = {
    default: 'text-muted-foreground',
    gold: 'text-ti-gold',
    cash: 'text-sky-400',
    profit: 'text-emerald-400',
    loss: 'text-rose-400',
    warning: 'text-amber-400',
  };
  return (
    <div
      className={cn(
        'relative rounded-xl p-4 sm:p-5 border transition-all hover-lift',
        accentMap[accent] || accentMap.default
      )}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-2">
        <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center bg-background/40', iconColorMap[accent] || iconColorMap.default)}>
          <Icon size={18} />
        </div>
        {badge && (
          <span className="text-[9px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-ti-gold/15 text-ti-gold">
            {badge}
          </span>
        )}
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md border border-ti-gold/40 text-ti-gold hover:bg-ti-gold/15 transition-colors"
            data-testid="edit-founding-btn"
            title="Sermaye Düzenle"
          >
            <Pencil size={11} />
            Düzenle
          </button>
        )}
      </div>
      <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1">{title}</p>
      <p className={cn(
        'font-heading font-bold tabular-nums leading-tight',
        accent === 'gold' ? 'text-2xl sm:text-3xl text-ti-gold' :
        accent === 'profit' ? 'text-2xl text-emerald-400' :
        accent === 'loss' ? 'text-2xl text-rose-400' :
        accent === 'warning' ? 'text-2xl text-amber-400' :
        'text-2xl text-foreground'
      )}>{value}</p>
      {subtitle && (
        <p className={cn(
          'text-[11px] mt-1.5 leading-snug',
          warning ? 'text-amber-400' : 'text-muted-foreground'
        )}>
          {warning && <AlertCircle size={11} className="inline mr-1 -mt-0.5" />}
          {subtitle}
        </p>
      )}
    </div>
  );
};

/**
 * ✅ Sağlama kutusu: Öz sermayenin nereden geldiğini (tüm zamanlar) gösterir ve
 * "Toplam Varlık − Kapora Emaneti = Öz Sermaye" eşitliğini kontrol eder.
 * Fark varsa nedenlerini listeler ve yöneticiye tek tıkla onarma imkânı verir.
 */
const ReconciliationPanel = ({ summary, canReconcile, onReconcile }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // ✅ Onarım mesajı sadece onarımın hemen ardından gösterilir; rakamlar sonradan değişirse
  // (ör. sermaye düzeltildi) eski "tamamlandı" mesajı ekranda kalıp kafa karıştırmasın.
  const lastDiff = useRef(summary?.reconciliation?.balance_difference);
  const justReconciled = useRef(false);
  useEffect(() => {
    const d = summary?.reconciliation?.balance_difference;
    if (d !== lastDiff.current) {
      lastDiff.current = d;
      if (justReconciled.current) justReconciled.current = false;
      else setResult(null);
    }
  }, [summary]);
  const [error, setError] = useState('');
  const rec = summary.reconciliation;
  if (!rec) return null;

  const founding = summary.founding_capital || 0;
  const retained = summary.retained_earnings ?? (summary.current_equity - founding);
  const diff = rec.balance_difference || 0;
  const balanced = rec.is_balanced;
  const legacyInst = rec.installment_double_count || 0;
  const fixable = (rec.unapplied_tx_count || 0) > 0 || (rec.purchase_mismatch_count || 0) > 0 || legacyInst > 0;
  const needsAttention = !balanced || legacyInst > 0;

  const run = async (absorb) => {
    if (!onReconcile) return;
    setBusy(true); setError(''); setResult(null);
    try {
      justReconciled.current = true;
      const r = await onReconcile(absorb);
      setResult(r);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Onarım yapılamadı. Lütfen tekrar deneyin.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={cn(
        'rounded-xl border px-4 py-3 text-xs space-y-2',
        !needsAttention ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/40 bg-amber-500/5'
      )}
      data-testid="finance-reconciliation"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground">
        <span className="font-semibold text-foreground">Öz Sermaye nasıl hesaplandı (tüm zamanlar):</span>
        <span className="tabular-nums">
          Başlangıç {formatCurrency(founding)} {retained >= 0 ? '+' : '−'} Birikmiş Kâr/Zarar {formatCurrency(Math.abs(retained))}
          {' '}= <span className="text-ti-gold font-semibold">{formatCurrency(summary.current_equity || 0)}</span>
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {balanced ? (
          <span className="flex items-center gap-1 text-emerald-400 font-medium">
            <CheckCircle2 size={13} />
            Sağlama tamam: Toplam Varlık − Kapora Emaneti = Öz Sermaye
          </span>
        ) : (
          <span className="flex items-center gap-1 text-amber-400 font-medium">
            <AlertCircle size={13} />
            Rakamlar tutmuyor: Toplam Varlık − Kapora Emaneti ile Öz Sermaye arasında {formatCurrency(Math.abs(diff))} fark var.
          </span>
        )}
      </div>

      {legacyInst > 0 && (
        <p className="text-amber-400">
          {legacyInst} vadeli satış eski yöntemle kaydedilmiş: satış bedeli ve taksitler iki kez kasaya ve kâra giriyor
          ({formatCurrency(rec.installment_double_amount || 0)}). "Kayıtları onar" ile tahsil edilmemiş tutar alacağa aktarılır.
        </p>
      )}

      {!balanced && (
        <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">
          {rec.unapplied_tx_count > 0 && (
            <li>{rec.unapplied_tx_count} işlem kasaya hiç işlenmemiş (net {formatCurrency(rec.unapplied_tx_net)}).</li>
          )}
          {rec.purchase_mismatch_count > 0 && (
            <li>{rec.purchase_mismatch_count} aracın alış fiyatı ile kasadaki alış kaydı farklı (toplam {formatCurrency(Math.abs(rec.purchase_mismatch_amount))}).</li>
          )}
          {Math.abs(rec.manual_cash_adjustments || 0) >= 1 && (
            <li>Elle yapılan kasa girişi/çıkışı/ayarlaması toplamı: {formatCurrency(rec.manual_cash_adjustments)}. Bunlar sermayeye yansımaz.</li>
          )}
          {founding <= 0 && (summary.cash_balance || 0) > 0 && (
            <li>
              Başlangıç sermayesi 0 ama kasada {formatCurrency(summary.cash_balance)} görünüyor. Bu para gerçekten varsa
              "Onar + kalan farkı sermayeye yansıt" ile sermaye olarak tanımlayın. Hesabı tamamen temizlemek istiyorsanız
              <strong> Ayarlar → Hesabı Sıfırla</strong> kasayı da sıfırlar.
            </li>
          )}
          {!fixable && Math.abs(rec.manual_cash_adjustments || 0) < 1 && (
            <li>Fark eski kayıtlardan geliyor. "Farkı sermayeye yansıt" ile kapatabilirsiniz.</li>
          )}
        </ul>
      )}

      {needsAttention && canReconcile && (
        <div className="flex flex-wrap gap-2 pt-1">
          {fixable && (
            <button
              type="button"
              disabled={busy}
              onClick={() => run(false)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md border border-ti-gold/50 text-ti-gold hover:bg-ti-gold/15 disabled:opacity-50"
              data-testid="reconcile-fix-btn"
            >
              <Wrench size={12} />
              {busy ? 'Onarılıyor…' : 'Kayıtları onar'}
            </button>
          )}
          {!balanced && <button
            type="button"
            disabled={busy}
            onClick={() => run(true)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md border border-border text-muted-foreground hover:bg-muted/40 disabled:opacity-50"
            data-testid="reconcile-absorb-btn"
            title="Kayıtları onarır, kalan farkı başlangıç sermayesine 'sermaye düzeltmesi' olarak ekler"
          >
            {busy ? 'İşleniyor…' : 'Onar + kalan farkı sermayeye yansıt'}
          </button>}
        </div>
      )}

      {result && (
        <p className="text-emerald-400">
          Onarım tamamlandı: {result.applied_transactions} işlem kasaya işlendi, {result.purchase_records_fixed} alış kaydı düzeltildi
          {result.installments_fixed ? `, ${result.installments_fixed} vadeli satış alacağa aktarıldı` : ''}
          {result.absorbed_into_founding ? `, ${formatCurrency(result.absorbed_into_founding)} sermayeye yansıtıldı` : ''}.
          Kalan fark: {formatCurrency(Math.abs(result.balance_difference_after || 0))}
        </p>
      )}
      {error && <p className="text-rose-400">{error}</p>}
    </div>
  );
};

export const FinanceSummaryCards = ({
  summary, onEditFounding, canEditFounding, isPeriodFiltered = false, canReconcile = false, onReconcile,
}) => {
  if (!summary) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="finance-summary-skeleton">
        {[1,2,3,4,5,6].map(i => (
          <div key={i} className="h-32 rounded-xl bg-card/50 border border-border animate-pulse" />
        ))}
      </div>
    );
  }

  const {
    founding_capital = 0,
    current_equity = 0,
    cash_balance = 0,
    stock_value = 0,
    net_profit = 0,
    total_assets = 0,
    stock_count = 0,
    consignment_count = 0,
    cash_is_negative,
    cash_explanation,
    equity_change = 0,
    equity_change_pct = 0,
    sold_count_period = 0,
    other_income_period = 0,
    founding_auto = false,
    open_deposits = 0,
    receivables = 0,
  } = summary;

  const equityUp = equity_change >= 0;
  const profitUp = net_profit >= 0;
  const showFoundingPlaceholder = founding_capital <= 0;

  return (
    <div className="space-y-3" data-testid="finance-summary">
      {/* Üst bant — Açıklama */}
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground bg-muted/30 rounded-lg px-3 py-2 border border-border">
        <Info size={12} className="text-ti-gold flex-shrink-0" />
        <span>
          Tüm değerler muhasebe prensiplerine göre hesaplanır. <span className="text-ti-gold font-semibold">Araç alımları gider değil, varlıktır.</span>
          {isPeriodFiltered && ' Net Kâr seçili döneme aittir; Öz Sermaye, Kasa, Stok ve Varlık her zaman güncel toplamdır.'}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* 1. Başlangıç Sermayesi */}
        <Card
          testId="card-founding-capital"
          title="Başlangıç Sermayesi"
          value={showFoundingPlaceholder ? '—' : formatCurrency(founding_capital)}
          subtitle={showFoundingPlaceholder
            ? (canEditFounding ? '👆 Tanımlamak için tıkla' : 'Henüz tanımlanmadı')
            : founding_auto ? 'Eski kayıtlardan otomatik hesaplandı' : 'Sabit öz sermaye referansı'}
          icon={Landmark}
          accent="default"
          onEdit={canEditFounding ? onEditFounding : undefined}
        />

        {/* 2. Güncel Öz Sermaye — En önemli kart */}
        <Card
          testId="card-current-equity"
          title="Güncel Öz Sermaye"
          value={formatCurrency(current_equity)}
          subtitle={
            founding_capital > 0
              ? `${equityUp ? '↑' : '↓'} ${formatCurrency(Math.abs(equity_change))} (${equityUp ? '+' : ''}${equity_change_pct}%)`
              : 'Şirketinizin gerçek değeri'
          }
          icon={Gem}
          accent="gold"
          badge="ÖZ SERMAYE"
        />

        {/* 3. Kasadaki Nakit */}
        <Card
          testId="card-cash-balance"
          title="Kasadaki Nakit"
          value={formatCurrency(cash_balance)}
          subtitle={cash_is_negative ? cash_explanation : 'Anlık nakit pozisyonu'}
          warning={cash_is_negative}
          icon={Wallet}
          accent={cash_is_negative ? 'warning' : 'cash'}
        />

        {/* 4. Stoktaki Araç Değeri */}
        <Card
          testId="card-stock-value"
          title="Stok Araç Değeri"
          value={formatCurrency(stock_value)}
          subtitle={`${stock_count} stok · ${consignment_count} konsinye (tam maliyetle)`}
          icon={Car}
          accent="default"
        />

        {/* 5. Net Kâr / Zarar */}
        <Card
          testId="card-net-profit"
          title={profitUp ? 'Net Kâr' : 'Net Zarar'}
          value={formatCurrency(net_profit)}
          badge={isPeriodFiltered ? 'SEÇİLİ DÖNEM' : 'TÜM ZAMANLAR'}
          subtitle={[
            sold_count_period > 0 ? `${sold_count_period} satış` : null,
            other_income_period > 0 ? `${formatCurrency(other_income_period)} diğer gelir` : null,
          ].filter(Boolean).join(' · ') || 'Araç satışı + diğer gelirler'}
          icon={profitUp ? TrendingUp : TrendingDown}
          accent={profitUp ? 'profit' : 'loss'}
        />

        {/* 6. Toplam Varlık */}
        <Card
          testId="card-total-assets"
          title="Toplam Varlık"
          value={formatCurrency(total_assets)}
          subtitle={[
            receivables > 0 ? `Nakit + Stok + ${formatCurrency(receivables)} alacak` : 'Nakit + Stok değeri',
            open_deposits > 0 ? `${formatCurrency(open_deposits)} kapora emanet` : null,
          ].filter(Boolean).join(' · ')}
          icon={Briefcase}
          accent="default"
        />
      </div>

      {/* ✅ Sağlama / mutabakat */}
      <ReconciliationPanel summary={summary} canReconcile={canReconcile} onReconcile={onReconcile} />

      {/* Founding Capital tıklanabilir */}
      {showFoundingPlaceholder && canEditFounding && (
        <button
          onClick={onEditFounding}
          className="w-full text-xs text-ti-gold hover:text-ti-gold-light underline-offset-2 hover:underline transition-colors py-1"
          data-testid="set-founding-btn"
        >
          Doğru muhasebe için → Başlangıç sermayenizi tanımlayın
        </button>
      )}
    </div>
  );
};

export default FinanceSummaryCards;
